"use client";

import { useState, useEffect, useCallback } from "react";
import {
  Star,
  Loader2,
  ShieldCheck,
  ThumbsUp,
  Upload,
  X,
  Play,
} from "lucide-react";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/Button";
import { useSession } from "next-auth/react";

interface ReviewMedia {
  url: string;
  type: "image" | "video";
}

interface Review {
  id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  isVerified: boolean;
  media: string[];
  storeResponse: string | null;
  storeResponseAt: string | null;
  helpfulCount: number;
  createdAt: string;
  user: { name: string | null };
}

function StarDisplay({ rating, size = "h-4 w-4" }: { rating: number; size?: string }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            size,
            i <= rating ? "text-amber-400 fill-amber-400" : "text-gray-200"
          )}
        />
      ))}
    </div>
  );
}

function isVideo(url: string): boolean {
  const lower = url.toLowerCase().split("?")[0];
  return [".mp4", ".mov", ".webm"].some((ext) => lower.endsWith(ext));
}

export default function ProductReviews({ productId }: { productId: string }) {
  const { toast } = useToast();
  const locale = useLocale();
  const { data: session } = useSession();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState<ReviewMedia | null>(null);

  // Form state
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [media, setMedia] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [helpfulSet, setHelpfulSet] = useState<Set<string>>(new Set());

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/products/${productId}/reviews`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setReviews(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (media.length + files.length > 5) {
      toast("Up to 5 photos/videos per review", "error");
      return;
    }
    setUploading(true);
    try {
      const newUrls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", "reviews");
        const res = await fetch("/api/upload", { method: "POST", body: fd });
        if (!res.ok) throw new Error();
        const { url } = await res.json();
        newUrls.push(url);
      }
      setMedia((prev) => [...prev, ...newUrls]);
    } catch {
      toast("Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const submitReview = async () => {
    if (!session?.user) {
      toast("Please sign in to leave a review", "error");
      return;
    }
    if (rating < 1 || rating > 5) {
      toast("Please select a rating", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/reviews", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId,
          rating,
          title: title || undefined,
          comment: comment || undefined,
          media,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to submit");
      }
      toast("Review submitted — pending approval", "success");
      setRating(5);
      setTitle("");
      setComment("");
      setMedia([]);
      setShowForm(false);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const markHelpful = async (id: string) => {
    if (helpfulSet.has(id)) return;
    setHelpfulSet((prev) => new Set(prev).add(id));
    try {
      const res = await fetch(`/api/reviews/${id}/helpful`, { method: "POST" });
      if (!res.ok) throw new Error();
      setReviews((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, helpfulCount: r.helpfulCount + 1 } : r
        )
      );
    } catch {
      // rollback
      setHelpfulSet((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary + Write button */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          {reviews.length > 0 ? (
            <div className="flex items-center gap-3">
              <StarDisplay
                rating={Math.round(
                  reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
                )}
                size="h-5 w-5"
              />
              <span className="text-sm text-brand-dark">
                {(
                  reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
                ).toFixed(1)}{" "}
                · {reviews.length} review{reviews.length !== 1 && "s"}
              </span>
            </div>
          ) : (
            <p className="text-sm text-brand-muted">No reviews yet</p>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShowForm((s) => !s)}
        >
          {showForm ? "Cancel" : "Write a Review"}
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="border border-gray-100 p-5 sm:p-6 rounded-sm bg-brand-cream/20">
          {!session?.user && (
            <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded mb-4">
              Please sign in to leave a review.
            </p>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-brand-dark mb-1.5 uppercase tracking-wider">
                Rating
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    className="p-1"
                    aria-label={`${i} stars`}
                  >
                    <Star
                      className={cn(
                        "h-6 w-6 transition-colors",
                        i <= rating
                          ? "text-amber-400 fill-amber-400"
                          : "text-gray-200"
                      )}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-dark mb-1.5 uppercase tracking-wider">
                Title (optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-dark mb-1.5 uppercase tracking-wider">
                Your review
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={5000}
                rows={4}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-dark mb-1.5 uppercase tracking-wider">
                Photos / Videos (optional, up to 5)
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {media.map((url, i) => (
                  <div
                    key={i}
                    className="relative h-16 w-16 rounded overflow-hidden border border-gray-200"
                  >
                    {isVideo(url) ? (
                      <div className="bg-black w-full h-full flex items-center justify-center">
                        <Play className="h-5 w-5 text-white" />
                      </div>
                    ) : (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={url}
                        alt=""
                        className="object-cover w-full h-full"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setMedia((prev) => prev.filter((_, j) => j !== i))
                      }
                      className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {media.length < 5 && (
                  <label className="h-16 w-16 border-2 border-dashed border-gray-200 hover:border-brand-gold flex items-center justify-center cursor-pointer rounded">
                    {uploading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-brand-gold" />
                    ) : (
                      <Upload className="h-5 w-5 text-brand-muted" />
                    )}
                    <input
                      type="file"
                      accept="image/*,video/mp4,video/quicktime"
                      multiple
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files)}
                    />
                  </label>
                )}
              </div>
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={submitReview}
              disabled={submitting || !session?.user}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Submit Review"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Review list */}
      {reviews.length > 0 && (
        <div className="space-y-5">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="border-b border-gray-100 pb-5 last:border-b-0"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <StarDisplay rating={review.rating} />
                    {review.isVerified && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                        <ShieldCheck className="h-3 w-3" />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                  {review.title && (
                    <p className="font-medium text-sm text-brand-dark">
                      {review.title}
                    </p>
                  )}
                  <p className="text-[11px] text-brand-muted">
                    {review.user?.name || "Customer"} ·{" "}
                    {new Date(review.createdAt).toLocaleDateString(locale)}
                  </p>
                </div>
              </div>
              {review.comment && (
                <p className="text-sm text-brand-dark/80 leading-relaxed mb-3 whitespace-pre-line">
                  {review.comment}
                </p>
              )}
              {review.media && review.media.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {review.media.map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() =>
                        setLightboxMedia({
                          url,
                          type: isVideo(url) ? "video" : "image",
                        })
                      }
                      className="relative h-20 w-20 rounded overflow-hidden border border-gray-200 hover:border-brand-gold transition-colors"
                    >
                      {isVideo(url) ? (
                        <div className="bg-black w-full h-full flex items-center justify-center">
                          <Play className="h-5 w-5 text-white" />
                        </div>
                      ) : (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={url}
                          alt=""
                          className="object-cover w-full h-full"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
              {review.storeResponse && (
                <div className="mt-3 border-l-2 border-brand-gold bg-brand-cream/30 px-4 py-3 rounded-r">
                  <p className="text-[10px] uppercase tracking-wider text-brand-gold font-medium mb-1">
                    Response from Rivky Wigs
                  </p>
                  <p className="text-sm text-brand-dark/80 whitespace-pre-line">
                    {review.storeResponse}
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={() => markHelpful(review.id)}
                disabled={helpfulSet.has(review.id)}
                className={cn(
                  "mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-full transition-colors",
                  helpfulSet.has(review.id)
                    ? "border-brand-gold bg-brand-gold/10 text-brand-gold"
                    : "border-gray-200 text-brand-muted hover:border-brand-gold hover:text-brand-gold"
                )}
              >
                <ThumbsUp className="h-3 w-3" />
                Helpful ({review.helpfulCount})
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxMedia && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxMedia(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxMedia(null)}
            className="absolute top-4 right-4 bg-white/10 text-white rounded-full p-2 hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {lightboxMedia.type === "video" ? (
              <video
                src={lightboxMedia.url}
                controls
                autoPlay
                className="max-h-[90vh] rounded"
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={lightboxMedia.url}
                alt=""
                className="max-h-[90vh] rounded object-contain"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

